import React from 'react';
import { Calendar, User } from 'lucide-react';

const Article2 = () => {
  return (
    <div className="pt-16 sm:pt-20">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-saffron-50 to-cream-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Articles & Resources</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover insights, tips, and stories about premium FMCG products and traditional Indian agriculture.
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Article Header */}
          <article className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Featured Image */}
            <div className="h-96 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1750579909774-ff40e6e1ca62"
                alt="Kashmir Saffron Fields"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div className="p-8 md:p-12">
              {/* Meta Information */}
              <div className="flex items-center gap-6 text-sm text-gray-500 mb-6">
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span>December 2024</span>
                </div>
                <div className="flex items-center gap-2">
                  <User size={16} />
                  <span>Hind Harvest Team</span>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Kashmir: Where Kesar Is Not Grown, but Guarded
              </h1>

              {/* Introduction */}
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-4 italic">
                  High in the Himalayas, where the air is thin, the soil is ancient, and time moves gently, lies Kashmir — a land long celebrated for beauty, poetry, and purity. Among its many treasures, one stands apart not for its abundance, but for its rarity: kesar, the finest saffron in the world.
                </p>
                <p className="text-lg text-gray-900 font-semibold mb-6">
                  This is not merely a crop of Kashmir. It is a legacy.
                </p>

                {/* Pampore Section */}
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Pampore: The Sacred Home of Saffron</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  At the heart of Kashmir's saffron story lies Pampore, a small town revered as the global cradle of authentic kesar. For over two millennia, Pampore's unique soil — rich in minerals and perfectly balanced in drainage — has nurtured saffron crocus flowers that bloom for just two fleeting weeks each year.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  There is no machinery here. No shortcuts. Only tradition, patience, and precision.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Each autumn, as dawn breaks over purple fields, farmers gently hand-pick the delicate crocus flowers. From every blossom, only three crimson stigmas are carefully separated — the very threads that become pure Kashmiri kesar. It takes nearly 150,000 flowers to produce one kilogram of saffron.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This is why Kashmiri kesar has always been precious. Not because it is expensive — but because it is earned.
                </p>

                {/* History Section */}
                <h2 className="text-2xl font-bold text-gray-900 mb-3">A History Steeped in Royalty & Ayurveda</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Historical texts trace Kashmiri saffron back to ancient Persia, Buddhist monks, and royal trade routes that once passed through the valley. Kings prized it, healers prescribed it, and poets wrote about it.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In Ayurveda, kesar is described as a <em>rasayana</em> — a rejuvenator for the mind, heart, skin, and soul. It was reserved for royalty, sacred rituals, and healing preparations. Even today, its use remains deeply ceremonial, woven into Kashmiri culture and identity.
                </p>

                {/* What Makes It Pure */}
                <h2 className="text-2xl font-bold text-gray-900 mb-3">What Makes Kashmiri Kesar Truly Pure</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Purity in kesar is not a claim — it is a consequence of geography. Kashmir's saffron is defined by:
                </p>
                <ul className="space-y-2 mb-4 list-disc pl-6">
                  <li className="text-gray-700">Deep crimson stigmas with no yellow or white parts</li>
                  <li className="text-gray-700">High crocin content, responsible for color, potency, and benefits</li>
                  <li className="text-gray-700">Intense natural aroma, never sharp or artificial</li>
                  <li className="text-gray-700">Slow sun-drying, preserving active compounds</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Unlike mass-produced saffron grown elsewhere, Kashmiri kesar is cultivated once a year, in limited quantities, under strict natural conditions that cannot be replicated.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This is why genuine Kashmiri kesar is rare — and why purity matters.
                </p>

                {/* Heritage Section */}
                <h2 className="text-2xl font-bold text-gray-900 mb-3">More Than a Spice — A Heritage Preserved</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  In a world of fast consumption and imitation, pure Kashmiri kesar remains untouched by haste. Every strand carries the story of the land, the farmer, the climate, and centuries of inherited wisdom.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  When you choose authentic kesar from Kashmir, you are not just choosing a wellness ingredient — you are choosing heritage over hype, purity over volume, and tradition over shortcuts.
                </p>

                {/* Closing Section */}
                <h2 className="text-2xl font-bold text-gray-900 mb-3">The Essence of Kashmir, In Every Strand</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  From the mist-covered fields of Pampore to your home, pure Kashmiri kesar travels unchanged — preserving its color, its power, and its soul.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  It is not manufactured. It is not improved. It is simply respected.
                </p>
                <p className="text-gray-900 font-semibold">
                  And that is what makes it extraordinary.
                </p>
              </div>
            </div>
          </article>

          {/* More Articles Coming Soon */}
          <div className="mt-12 bg-gradient-to-br from-saffron-50 to-cream-100 rounded-2xl p-8 text-center">
            <p className="text-lg font-semibold text-gray-900 mb-2">More Articles Coming Soon</p>
            <p className="text-gray-600">
              Stay tuned for more insightful articles about traditional farming practices, health benefits, recipes, and India's rich agricultural heritage.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Article2;
